import { T } from 'gt-react';
import { TASK_TYPE_ESCALATION_TARGET_TYPES } from '@erp/shared-types';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import {
  TaskTypeAllocationPicker,
  TaskTypeUserGroupPicker,
  TaskTypeUserPicker,
} from './TaskTypeDefaultAssigneeField';
import { FieldBlock } from './TaskTypeEditorSections';
import type {
  TaskTypeEscalationTargetEditorState,
  TaskTypeEscalationTargetMode,
} from './taskTypeAdmin';

function isEscalationTargetMode(
  value: string,
): value is TaskTypeEscalationTargetMode {
  const kinds: readonly string[] = TASK_TYPE_ESCALATION_TARGET_TYPES;
  return value === 'none' || kinds.includes(value);
}

/**
 * Escalation target controls for a task type: a kind toggle (None / User /
 * Team) and the matching picker — plus, for a team, the allocation method
 * that picks one member per hand-off. No Agent kind: an escalation is by
 * definition a hand-off to a human. Switching kinds keeps the other kind's
 * last pick in draft state; `buildTaskTypeEscalationTarget` drops the
 * inactive id on save.
 */
export function TaskTypeEscalationTargetField({
  editor,
  disabled,
  onChange,
}: {
  editor: TaskTypeEscalationTargetEditorState;
  disabled?: boolean;
  onChange: (next: TaskTypeEscalationTargetEditorState) => void;
}) {
  return (
    <FieldBlock
      label={<T context="task type form field label">Escalation target</T>}
      description={
        <T context="hint under the escalation target controls on a task type">
          When an agent working a task of this type needs a person (the task
          moves to Needs input), it is handed to this user or a member of this
          team. Without one, it goes to the task&apos;s creator, then the
          workflow&apos;s triggerer or creator, then a run watcher. A valid
          assignee the agent names itself still wins.
        </T>
      }
    >
      <div className="flex flex-col gap-2">
        <ToggleGroup
          type="single"
          value={editor.escalationTargetMode}
          onValueChange={(value) => {
            // Re-clicking the active item emits ''; keep a kind selected.
            if (isEscalationTargetMode(value)) {
              onChange({ ...editor, escalationTargetMode: value });
            }
          }}
          variant="outline"
          size="sm"
          disabled={disabled}
          className="w-fit"
        >
          <ToggleGroupItem value="none">
            <T context="escalation target kind toggle, no configured target">
              None
            </T>
          </ToggleGroupItem>
          <ToggleGroupItem value="user">
            <T context="escalation target kind toggle, a person">User</T>
          </ToggleGroupItem>
          <ToggleGroupItem value="user_group">
            <T context="escalation target kind toggle, a user group whose members share hand-offs">
              Team
            </T>
          </ToggleGroupItem>
        </ToggleGroup>

        {editor.escalationTargetMode === 'user' && (
          <TaskTypeUserPicker
            value={editor.escalationTargetUserId}
            disabled={disabled}
            onChange={(escalationTargetUserId) =>
              onChange({ ...editor, escalationTargetUserId })
            }
          />
        )}

        {editor.escalationTargetMode === 'user_group' && (
          <>
            <TaskTypeUserGroupPicker
              value={editor.escalationTargetUserGroupId}
              disabled={disabled}
              onChange={(escalationTargetUserGroupId) =>
                onChange({ ...editor, escalationTargetUserGroupId })
              }
            />
            <TaskTypeAllocationPicker
              value={editor.escalationTargetAllocation}
              disabled={disabled}
              onChange={(escalationTargetAllocation) =>
                onChange({ ...editor, escalationTargetAllocation })
              }
            />
          </>
        )}
      </div>
    </FieldBlock>
  );
}
